import { create } from "zustand";
// import { persist } from "zustand/middleware";

import type { Tournament as EntitiesTournament } from "@/entities/tournament/model/types";
import {
  tournamentApi,
  type Tournament as ApiTournament,
  type CreateTournamentRequest,
  type UpdateTournamentRequest,
} from "@/shared/api/tournament";
import type { GoodLuckCoreDTOsTournamentResponse } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/TournamentResponse";
import { preserveIfEqual } from "@/shared/lib/state-change-guard";

// ApiTournament (shared/api/tournament.ts) — устаревший ручной подтип ответа бэкенда,
// не отражает реальный контракт (KAN-96). GoodLuckCoreDTOsTournamentResponse — актуальная
// Kubb-схема; пересекаем типы, чтобы читать реальные поля без `as any` на каждое из них.
type RawTournamentResponse = ApiTournament & GoodLuckCoreDTOsTournamentResponse;

interface TournamentState {
  tournaments: EntitiesTournament[];
  currentTournament: EntitiesTournament | null;
  /**
   * Идентификатор, по которому загружали текущий турнир: id ИЛИ slug — ровно то,
   * что пришло из URL. Нужен, чтобы отличить «перезапрос того же турнира» от
   * «перехода на другой». По самому объекту это не определить: он хранит только
   * id, а страница поллит по slug, и сравнение всегда давало «другой турнир».
   */
  currentTournamentKey: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setTournaments: (tournaments: EntitiesTournament[]) => void;
  setCurrentTournament: (tournament: EntitiesTournament | null) => void;
  fetchTournaments: (params?: {
    status?: string;
    game?: string;
    page?: number;
    limit?: number;
  }) => Promise<void>;
  fetchTournamentById: (tournamentId: string) => Promise<void>;
  createTournament: (data: CreateTournamentRequest) => Promise<string>;
  updateTournament: (
    tournamentId: string,
    data: UpdateTournamentRequest,
  ) => Promise<EntitiesTournament>;
  deleteTournament: (tournamentId: string) => Promise<void>;
  registerForTournament: (
    tournamentId: string,
    teamId?: string,
  ) => Promise<void>;
  unregisterFromTournament: (tournamentId: string) => Promise<void>;
  clearError: () => void;
}

// Keep backend statuses for correct UX (filters + badges)
const mapStatus = (apiStatus?: string | null): EntitiesTournament["status"] => {
  const s = apiStatus?.toLowerCase?.() ?? "";
  switch (s) {
    case "draft":
      return "Draft";
    case "upcoming":
      return "Upcoming";
    case "registrationopen":
      return "RegistrationOpen";
    case "registrationclosed":
      return "RegistrationClosed";
    case "awaitingbracket":
      return "AwaitingBracket";
    case "inprogress":
      return "InProgress";
    case "ongoing":
      return "Ongoing";
    case "completed":
    case "finished":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    // legacy values from old frontend/backends
    case "active":
      return "InProgress";
    case "completed_legacy":
      return "Completed";
    case "cancelled_legacy":
      return "Cancelled";
    default:
      return "Unknown";
  }
};

// Mapper: API Tournament -> Entities Tournament (normalize currency, optional fields)
const normalizeWinnerMode = (
  value: unknown,
): EntitiesTournament["manualWinnerMode"] => {
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    // Реальные значения API (WinnerPromotionMode → lower): desktop/manual/discord/riotapi/replay.
    if (normalized === "desktop") return "desktop";
    // KAN-354: подтверждение победы реплеем.
    if (normalized === "replay") return "replay";
    if (normalized.includes("manual")) return "manual";
    if (normalized.includes("auto")) return "auto";
    // discord/riot(api) не входят в WinnerControlMode — это не desktop-режим,
    // трактуем как ручное продвижение (организатор фиксирует результаты).
    if (normalized.includes("discord") || normalized.includes("riot"))
      return "manual";
  }
  if (typeof value === "boolean") {
    return value ? "manual" : "auto";
  }
  return undefined;
};

const mapTournament = (t: ApiTournament): EntitiesTournament => {
  const raw = t as RawTournamentResponse;

  // autoStartMatches/autoManageMatches: не входят в актуальный TournamentResponse —
  // легаси-имена поля, которых бэкенд больше не отдаёт. Оставлено как fallback
  // на случай старых закэшированных ответов.
  const legacy = t as ApiTournament & Record<string, unknown>;
  const autoStartMatches =
    (legacy["autoStartMatches"] as boolean | undefined) ??
    (legacy["autoManageMatches"] as boolean | undefined);

  const manualWinnerMode =
    // ПРИОРИТЕТ: реальное поле API (TournamentResponse.manualWinnerMode).
    // Раньше его не читали → всегда undefined → settings-tab дефолтил в "desktop"
    // (режим продвижения «сбрасывался» на desktop после перезагрузки).
    normalizeWinnerMode(raw.manualWinnerMode) ??
    // winnerManagementMode/bracketWinnerMode/matchResultMode/promotionMode/bracketMode/
    // manualWinnerControl — легаси-имена поля вне текущего TournamentResponse,
    // fallback на случай старых закэшированных ответов.
    normalizeWinnerMode(
      legacy["winnerManagementMode"] ??
        legacy["bracketWinnerMode"] ??
        legacy["matchResultMode"] ??
        legacy["promotionMode"] ??
        legacy["bracketMode"],
    ) ??
    normalizeWinnerMode(legacy["manualWinnerControl"]) ??
    (typeof autoStartMatches === "boolean"
      ? autoStartMatches
        ? "auto"
        : "manual"
      : undefined);

  const organizerRaw = raw.organizer;
  const organizerFromApi = organizerRaw
    ? {
        id: organizerRaw.id ?? undefined,
        name: organizerRaw.name ?? undefined,
        avatar: organizerRaw.avatar || "/avatars/default-avatar.svg",
        ...(organizerRaw.url ? { url: organizerRaw.url } : {}),
        ...(organizerRaw.currentUserRole
          ? { currentUserRole: organizerRaw.currentUserRole }
          : {}),
        ...(organizerRaw.hasPremiumFeatures !== undefined
          ? { hasPremiumFeatures: organizerRaw.hasPremiumFeatures }
          : {}),
      }
    : undefined;

  // organizationId: не входит в TournamentResponse (только вложенный organizer.id) —
  // fallback на случай старых закэшированных ответов с плоским полем.
  const organizationId =
    (legacy["organizationId"] as string | undefined) ??
    organizerFromApi?.id ??
    undefined;

  return {
    // KAN-364: раньше маппер собирал объект ПО БЕЛОМУ СПИСКУ — всё, что не
    // перечислено ниже, молча исчезало, а финальный `as unknown as` глушил
    // проверку типов. За историю файла так терялись драфт-поля (KAN-113),
    // Arena-поля, момент открытия лобби (KAN-358) и statsMode — каждый раз это
    // находили постфактум и дописывали ещё одну строку.
    //
    // Хуже, чем «поле не показывается»: вкладка настроек читает значение, не
    // получает его, подставляет дефолт и отправляет этот дефолт обратно при
    // сохранении. То есть организатор, сохранив любое поле, молча сбрасывал
    // сбор статистики, лист ожидания, окна регистрации и режим Discord.
    //
    // Теперь по умолчанию проезжает ВСЁ, что прислал бэкенд, а перечисленное
    // ниже — только нормализация, легаси-фолбэки и переименования, и оно
    // перекрывает сырые значения.
    ...raw,
    id: t.id,
    name: t.name,
    description: t.description,
    createdById:
      raw.createdById ?? (legacy["creatorId"] as string | undefined) ?? undefined,
    createdByName:
      raw.createdByName ??
      (legacy["createdBy"] as string | undefined) ??
      undefined,
    status: mapStatus(t.status),
    backendStatus: t.status,
    format: raw.format || t.type,
    type: raw.type ?? undefined,
    bracketType: raw.bracketType ?? undefined,
    defaultBestOf: raw.defaultBestOf ?? undefined,
    game: raw.game || "League of Legends",
    prizePool: t.prizePool,
    prizeDescription: raw.prizeDescription ?? undefined,
    prizeDistribution: raw.prizeDistribution ?? undefined,
    officialStreamChannel: raw.officialStreamChannel ?? undefined,
    showPlayerStreamers: raw.showPlayerStreamers ?? true,
    showOfficialStream: raw.showOfficialStream ?? true,
    currency: raw.currency || "RUB",
    startDate: t.startDate,
    endDate: t.endDate,
    maxTeams: raw.maxTeams || t.maxPlayers,
    currentTeams: raw.currentTeams || raw.registeredPlayersCount,
    registeredPlayersCount: raw.registeredPlayersCount ?? undefined,
    // Реальный лимит регистраций (MaxPlayers на бэке); null = без лимита.
    playerLimit: raw.maxPlayers ?? undefined,
    location: raw.location || "Online",
    minTeamSize: raw.minTeamSize ?? undefined,
    maxTeamSize: raw.maxTeamSize ?? undefined,
    checkInWindowMinutes: raw.checkInWindowMinutes ?? undefined,
    // checkInStartTime/checkInEndTime: не входят в TournamentResponse — окно чек-ина
    // сейчас описывается только checkInWindowMinutes; оставлено на случай появления полей.
    checkInStartTime:
      (legacy["checkInStartTime"] as string | undefined) ?? undefined,
    checkInEndTime: (legacy["checkInEndTime"] as string | undefined) ?? undefined,
    organizationId,
    organizer: organizerFromApi,
    image: raw.image || "/covers/default-cover.svg",
    autoStartMatches:
      typeof autoStartMatches === "boolean" ? autoStartMatches : undefined,
    manualWinnerMode,
    registeredUserIds: [],
    rules: t.rules ?? "",
    // teams: не входит в TournamentResponse (список команд приходит отдельными эндпоинтами) —
    // fallback на случай старых закэшированных ответов.
    teams: (legacy["teams"] as EntitiesTournament["teams"]) ?? [],
    schedule: [],
    scheduleDays: raw.scheduleDays || undefined,
    minRequiredRank: raw.minRequiredRank ?? undefined,
    draftStartTime: raw.draftStartTime ?? undefined,
    draftStartAt: raw.draftStartAt ?? undefined,
    draftStartedAt: raw.draftStartedAt ?? undefined,
    // Драфт-поля (KAN-113). Без явного проброса маппер их срезал:
    // frozen/canEdit считались от undefined, а сохранённые стоимости
    // по рангам и бюджет капитана подменялись дефолтами.
    isDraftStarted: Boolean(raw.isDraftStarted),
    isDraftCompleted: Boolean(raw.isDraftCompleted),
    // KAN-358: без этой строки поле срезалось маппером, `lobbyOpened` на фронте был
    // вечно false — кнопка «Открыть лобби драфта» не исчезала после открытия, а шаг
    // «ждём готовности капитанов» не показывался никогда. Тип поле знал, но
    // финальный `as unknown as` глушил проверку — компилятор об этом не сказал.
    draftLobbyOpenedAtUtc: raw.draftLobbyOpenedAtUtc ?? undefined,
    isDraftBlocked: Boolean(raw.isDraftBlocked),
    draftBlockedReason: raw.draftBlockedReason ?? undefined,
    draftBlockedAtUtc: raw.draftBlockedAtUtc ?? undefined,
    maxTeamsInDraft: raw.maxTeamsInDraft ?? undefined,
    draftCaptainBudget: raw.draftCaptainBudget ?? undefined,
    draftRankCosts: raw.draftRankCosts ?? undefined,
    draftRankCostEntries: raw.draftRankCostEntries ?? undefined,
    // draftType: не входит в TournamentResponse — fallback на случай старых
    // закэшированных ответов.
    draftType: (legacy["draftType"] as string | undefined) ?? undefined,
    // draftOrderMode: тоже не входит в актуальную Kubb-схему (регенерация не
    // гонялась после добавления поля на бэке) — тот же fallback, что у draftType.
    draftOrderMode:
      (legacy["draftOrderMode"] as string | undefined) ?? undefined,
    enableCheckIn: raw.enableCheckIn ?? undefined,
    draftPickTimeoutSeconds: raw.draftPickTimeoutSeconds ?? undefined,
    draftTimeoutAction: raw.draftTimeoutAction ?? undefined,
    registrationApprovalMode: raw.registrationApprovalMode ?? undefined,
    capabilities: raw.capabilities ?? undefined,
    hasBracket: Boolean(raw.hasBracket),
    isBracketPublished: Boolean(raw.isBracketPublished),
    bracketStatus: raw.bracketStatus ?? undefined,
    customRegistrationFields: raw.customRegistrationFields ?? undefined,
    // Arena-поля (в т.ч. Random-режим). Без явного проброса маппер их срезал,
    // из-за чего страница турнира всегда считала арену PreMade.
    arenaTeamMode: raw.arenaTeamMode ?? undefined,
    arenaLobbyCount: raw.arenaLobbyCount ?? undefined,
    arenaGamesCount: raw.arenaGamesCount ?? undefined,
    arenaTeamCount: raw.arenaTeamCount ?? undefined,
    arenaAssemblyState: raw.arenaAssemblyState ?? undefined,
    arenaCurrentRound: raw.arenaCurrentRound ?? undefined,
    arenaTasksDocUrl: raw.arenaTasksDocUrl ?? undefined,
    requireRiotAccount: raw.requireRiotAccount ?? undefined,
    enableThirdPlaceMatch: raw.enableThirdPlaceMatch ?? undefined,
    enableGrandFinal: raw.enableGrandFinal ?? undefined,
    startTimeTentative: raw.startTimeTentative ?? undefined,
  } as unknown as EntitiesTournament;
};

let tournamentFetchGeneration = 0;

export const useTournamentStore = create<TournamentState>((set, get) => ({
  tournaments: [],
  currentTournament: null,
  currentTournamentKey: null,
  isLoading: false,
  error: null,

  setTournaments: (tournaments) => set({ tournaments }),

  setCurrentTournament: (tournament) => set({ currentTournament: tournament }),

  fetchTournaments: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const tournaments = await tournamentApi.getTournaments(params);
      // KAN-236: сохраняем прежнюю ссылку при неизменных данных, чтобы поллинг
      // не ререндерил подписчиков списка турниров впустую.
      const mapped = tournaments.map(mapTournament);
      set({
        tournaments: preserveIfEqual(get().tournaments, mapped),
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch tournaments",
        isLoading: false,
      });
    }
  },

  fetchTournamentById: async (tournamentId) => {
    const generation = ++tournamentFetchGeneration;
    const prev = get().currentTournament;
    // Сравниваем с КЛЮЧОМ прошлой загрузки, а не с полями объекта.
    //
    // Здесь был неприятный баг: страница турнира поллит каждые 20 секунд по тому,
    // что стоит в URL, а там после редиректа slug. Объект же хранит только id
    // (mapTournament slug не пробрасывает), поэтому `prev?.id === tournamentId`
    // не совпадало НИКОГДА. Каждый тик поллинга сбрасывал currentTournament в null
    // и поднимал isLoading — страница целиком уходила в скелетон и возвращалась.
    // Со стороны это выглядело как самопроизвольная перезагрузка раз в 20 секунд.
    const isSameTournament =
      get().currentTournamentKey === tournamentId ||
      prev?.id === tournamentId;
    // Keep previous tournament data during refetch to avoid unmounting child components
    set({
      isLoading: !isSameTournament,
      error: null,
      currentTournament: isSameTournament ? prev : null,
    });
    try {
      const tournament = await tournamentApi.getTournamentById(tournamentId);
      if (generation !== tournamentFetchGeneration) return;
      // KAN-236: горячий путь — на этот стор подписаны 7 страниц турнира; при
      // неизменных данных сохраняем прежнюю ссылку, иначе 20-сек поллинг ререндерит
      // их все впустую.
      const mapped = mapTournament(tournament);
      set({
        currentTournament: preserveIfEqual(get().currentTournament, mapped),
        currentTournamentKey: tournamentId,
        isLoading: false,
      });
    } catch (error) {
      if (generation !== tournamentFetchGeneration) return;
      set({
        error:
          error instanceof Error ? error.message : "Failed to fetch tournament",
        isLoading: false,
        currentTournament: null,
        currentTournamentKey: null,
      });
    }
  },

  createTournament: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const result = await tournamentApi.createTournament(data);
      set({ isLoading: false });
      return result.id;
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to create tournament",
        isLoading: false,
      });
      throw error;
    }
  },

  updateTournament: async (tournamentId, data) => {
    set({ isLoading: true, error: null });
    try {
      const tournament = await tournamentApi.updateTournament(
        tournamentId,
        data,
      );
      set((state) => ({
        tournaments: state.tournaments.map((t) =>
          t.id === tournamentId ? mapTournament(tournament) : t,
        ),
        currentTournament:
          state.currentTournament?.id === tournamentId
            ? mapTournament(tournament)
            : state.currentTournament,
        isLoading: false,
      }));
      return mapTournament(tournament);
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to update tournament",
        isLoading: false,
      });
      throw error;
    }
  },

  deleteTournament: async (tournamentId) => {
    set({ isLoading: true, error: null });
    try {
      await tournamentApi.deleteTournament(tournamentId);
      set((state) => ({
        tournaments: state.tournaments.filter((t) => t.id !== tournamentId),
        currentTournament:
          state.currentTournament?.id === tournamentId
            ? null
            : state.currentTournament,
        isLoading: false,
      }));
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to delete tournament",
        isLoading: false,
      });
      throw error;
    }
  },

  registerForTournament: async (tournamentId, teamId) => {
    set({ isLoading: true, error: null });
    try {
      await tournamentApi.registerForTournament(tournamentId, teamId);
      // Refresh tournament data
      await get().fetchTournamentById(tournamentId);
      set({ isLoading: false });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to register for tournament",
        isLoading: false,
      });
      throw error;
    }
  },

  unregisterFromTournament: async (tournamentId) => {
    set({ isLoading: true, error: null });
    try {
      await tournamentApi.unregisterFromTournament(tournamentId);
      // Refresh tournament data
      await get().fetchTournamentById(tournamentId);
      set({ isLoading: false });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to unregister from tournament",
        isLoading: false,
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
